import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import type { DateSelectArg, EventClickArg } from "@fullcalendar/core";
import { Modal } from "../../utils/Modal";
import { Form } from "../../core/Form";
import { initAdminFormControls } from "../global/initAdminFormControls";

function escHtml(str: string): string {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
}

function toIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

// FC exclusive end → 실제 종료일 (end - 1일)
function fcEndToReal(end: Date): string {
    const d = new Date(end);
    d.setDate(d.getDate() - 1);
    return toIsoDate(d);
}

// 중복 오픈 가드 — dateClick+select 동시 발화 등으로 모달이 2개 쌓이는 것을 방지.
let calModalOpen = false;

export async function openCalModal(
    url: string,
    title: string,
    fragmentUrl: string,
    fragmentEditBase: string,
): Promise<void> {
    if (calModalOpen) return;
    calModalOpen = true;

    let html: string;
    try {
        const res = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } });
        if (!res.ok) { calModalOpen = false; await Modal.alert("폼을 불러오지 못했습니다."); return; }
        html = await res.text();
    } catch {
        calModalOpen = false;
        await Modal.alert("네트워크 오류가 발생했습니다.");
        return;
    }

    const root = document.getElementById("app-modal-root") ?? (() => {
        const el = document.createElement("div");
        el.id = "app-modal-root";
        document.body.appendChild(el);
        return el;
    })();

    const backdrop = document.createElement("div");
    backdrop.className = "app-modal-backdrop";

    const box = document.createElement("div");
    box.className = "app-modal-box app-modal-box--wide cal-post-modal";
    box.innerHTML = `
        <div class="app-modal-header">
            <h2 class="app-modal-title">${escHtml(title)}</h2>
            <button type="button" class="app-modal-close" data-cal-close aria-label="닫기">
                <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
        </div>
        <div class="app-modal-body app-modal-body--scroll cal-post-modal__body" data-lenis-prevent></div>
        <div class="app-modal-footer cal-post-modal__footer">
            <button type="button" class="app-modal-btn" data-cal-close>취소</button>
            <button type="submit" form="cal-modal-form" class="app-modal-btn app-modal-btn--primary">저장</button>
        </div>
    `;

    const body = box.querySelector<HTMLElement>(".cal-post-modal__body")!;
    body.innerHTML = html;

    // CSRF 동기화 — 프래그먼트는 모달 열 때 GET 으로 받아 현재 세션 토큰을 담고 있다.
    // 페이지 로드 시점의 meta/_token(헤더 등)이 더 오래됐을 수 있으므로(세션 토큰 변경 시),
    // 최신 토큰으로 맞춰 모달 내 삭제·업로드 AJAX 가 유효 토큰을 쓰게 한다. (저장은 이미 프래그먼트 토큰 사용)
    const freshToken = body.querySelector<HTMLInputElement>('input[name="_token"]')?.value;
    if (freshToken) {
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.setAttribute("content", freshToken);
        document.querySelectorAll<HTMLInputElement>('input[name="_token"]').forEach((i) => { i.value = freshToken; });
    }

    backdrop.appendChild(box);
    root.appendChild(backdrop);

    // 페이지 폼과 동일한 컨트롤 초기화 — 언어탭·리치에디터·업로더·셀렉트·태그 등 일괄.
    initAdminFormControls(body);

    const close = (): void => { backdrop.remove(); calModalOpen = false; };
    box.querySelectorAll("[data-cal-close]").forEach(btn => btn.addEventListener("click", close));
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    box.addEventListener("click", (e) => e.stopPropagation());

    const form = body.querySelector<HTMLFormElement>("form[data-cal-modal-form]");
    if (form) {
        new Form(form, {
            onSuccess() { close(); location.reload(); },
            onFail(msg) { if (msg) void Modal.alert(msg); },
            requestFailedMessage: "요청에 실패했습니다.",
            invalidResponseMessage: "응답을 처리할 수 없습니다.",
        }).init();
    }

    const delBtn = body.querySelector<HTMLButtonElement>("[data-cal-modal-delete]");
    if (delBtn) {
        delBtn.addEventListener("click", async () => {
            const confirmed = await Modal.confirm(delBtn.dataset.confirm ?? "삭제할까요?");
            if (!confirmed) return;
            const actionUrl = delBtn.dataset.action ?? "";
            if (!actionUrl) return;
            const csrf =
                document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
                body.querySelector<HTMLInputElement>('input[name="_token"]')?.value ?? "";
            try {
                const res = await fetch(actionUrl, {
                    method: "POST",
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                        "X-CSRF-TOKEN": csrf,
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    body: `_method=DELETE&_token=${encodeURIComponent(csrf)}`,
                });
                if (res.ok) { close(); location.reload(); }
                else { await Modal.alert("삭제에 실패했습니다."); }
            } catch {
                await Modal.alert("네트워크 오류가 발생했습니다.");
            }
        });
    }
}

export function initAdminPostCalendar(page: HTMLElement): void {
    const el = page.querySelector<HTMLElement>("#admin-fc-root");
    if (!el) return;

    const eventsRaw      = el.dataset.fcEvents ?? "[]";
    const fragmentUrl    = el.dataset.fragmentUrl ?? "";
    const fragmentEditBase = el.dataset.fragmentEditBase ?? "";

    let events: object[] = [];
    try { events = JSON.parse(eventsRaw) as object[]; } catch { /* 빈 배열 유지 */ }

    const calendar = new Calendar(el, {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: "dayGridMonth",
        locale: "ko",
        firstDay: 0,
        selectable: true,
        selectMirror: true,
        editable: false,
        height: "auto",
        fixedWeekCount: false,          // 불필요한 6번째(빈) 주 제거
        eventDisplay: "block",          // 단일/다일 이벤트 모두 솔리드 블록으로 통일
        dayMaxEvents: 4,                // 셀당 4개 초과 시 "+N개" 로 접기
        moreLinkText: (n: number): string => `+${n}개`,
        headerToolbar: {
            left:   "prev,next today",
            center: "title",
            right:  "dayGridMonth,timeGridWeek,timeGridDay",
        },
        buttonText: { today: "오늘", month: "월", week: "주", day: "일" },
        events,

        // 단일 클릭/드래그 모두 select 로 처리한다. (dateClick 을 함께 두면 단일 클릭 시
        // dateClick+select 가 동시 발화해 모달이 2개 쌓이므로 dateClick 은 사용하지 않는다.)
        select(info: DateSelectArg) {
            const endStr = fcEndToReal(info.end);
            const sep = fragmentUrl.includes("?") ? "&" : "?";
            const isSameDay = info.startStr === endStr;
            const label = isSameDay ? info.startStr : `${info.startStr} ~ ${endStr}`;
            void openCalModal(
                `${fragmentUrl}${sep}start=${info.startStr}&end=${endStr}`,
                `게시글 추가 — ${label}`,
                fragmentUrl,
                fragmentEditBase,
            );
        },

        eventClick(info: EventClickArg) {
            info.jsEvent.preventDefault();
            const postId = String((info.event.extendedProps as Record<string, unknown>).postId ?? "");
            if (!postId) return;
            const url = fragmentEditBase.replace(/\/0\/form-fragment/, `/${postId}/form-fragment`);
            void openCalModal(url, "게시글 편집", fragmentUrl, fragmentEditBase);
        },
    });

    calendar.render();
}
